import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { pricingConfig } from '../data/pricing';
import type { BundleId } from '../data/pricing';
import { getBundle, priceBundle } from '../utils/pricing';
import { buildCheckoutUrl, captureAttribution, metaContents, normalizeOrder, orderTotals } from '../utils/checkout';
import type { OrderLine } from '../utils/checkout';
import { track } from '../utils/analytics';

/**
 * Pre-checkout order. `primary` is the system the customer chose (or upgraded to);
 * `addOns` are additional complete systems kept alongside it.
 */
export type FunnelState = {
  original: OrderLine;
  primary: OrderLine;
  addOns: OrderLine[];
} | null;
type FunnelContextValue = {
  state: FunnelState;
  /** Final normalized order (what goes to Shopify). */
  order: OrderLine[];
  startSystem: (bundleId: BundleId) => void;
  startIndividual: (qty: number) => void;
  upgradeTo: (bundleId: BundleId) => void;
  undoUpgrade: () => void;
  addSystem: (bundleId: BundleId) => void;
  setAddOnQty: (bundleId: BundleId, qty: number) => void;
  replaceOrder: (lines: OrderLine[]) => void;
  checkout: () => void;
  close: () => void;
};
const FunnelContext = createContext<FunnelContextValue | null>(null);
function trackAdd(bundleId: BundleId, qty: number, source: string) {
  const b = getBundle(bundleId);
  const price = priceBundle(b).total;
  track('add_to_cart', {
    source,
    bundleId,
    content_ids: [b.sku],
    content_name: b.name,
    content_type: 'product',
    contents: [{
      id: b.sku,
      quantity: qty,
      item_price: price
    }],
    num_items: b.qty * qty,
    value: Math.round(price * qty * 100) / 100,
    currency: pricingConfig.currency
  });
}

/** Orchestrates "Complete your MarineForceLED system" between any purchase button and Shopify checkout. */
export function FunnelProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<FunnelState>(null);
  useEffect(() => {
    captureAttribution();
  }, []);
  const order = useMemo(() => state ? normalizeOrder([state.primary, ...state.addOns]) : [], [state]);
  const open = (line: OrderLine) => {
    track('bundle_selected', {
      bundleId: line.bundleId,
      qty: line.qty
    });
    trackAdd(line.bundleId, line.qty, 'selection');
    setState({
      original: line,
      primary: line,
      addOns: []
    });
  };
  const startSystem = (bundleId: BundleId) => open({
    bundleId,
    qty: 1
  });
  const startIndividual = (qty: number) => {
    const exact = pricingConfig.bundles.find(b => b.qty === qty);
    open(exact ? {
      bundleId: exact.id,
      qty: 1
    } : {
      bundleId: 'sys1',
      qty
    });
  };
  const upgradeTo = (bundleId: BundleId) => setState(s => {
    if (!s) return s;
    track('upgrade_offer', {
      action: 'accept',
      from: s.primary.bundleId,
      to: bundleId
    });
    trackAdd(bundleId, 1, 'upgrade');
    // Upgrade REPLACES the selected system — never both.
    return {
      ...s,
      primary: {
        bundleId,
        qty: 1
      }
    };
  });
  const undoUpgrade = () => setState(s => {
    if (!s) return s;
    track('upgrade_offer', {
      action: 'undo',
      to: s.original.bundleId
    });
    return {
      ...s,
      primary: s.original
    };
  });
  const addSystem = (bundleId: BundleId) => setState(s => {
    if (!s) return s;
    track('add_another_system', {
      bundleId
    });
    trackAdd(bundleId, 1, 'add_another_system');
    return {
      ...s,
      addOns: normalizeOrder([...s.addOns, {
        bundleId,
        qty: 1
      }])
    };
  });
  const setAddOnQty = (bundleId: BundleId, qty: number) => setState(s => {
    if (!s) return s;
    const rest = s.addOns.filter(l => l.bundleId !== bundleId);
    return {
      ...s,
      addOns: normalizeOrder(qty > 0 ? [...rest, {
        bundleId,
        qty
      }] : rest)
    };
  });

  /** Smart System Savings switch — replaces the whole order with the better-value configuration. */
  const replaceOrder = (lines: OrderLine[]) => setState(s => {
    if (!s) return s;
    const [first, ...rest] = normalizeOrder(lines);
    if (!first) return s;
    // Keep a multi-copy line as primary with its quantity; everything else becomes add-ons.
    track('smart_savings', {
      action: 'accept',
      lines: lines.map(l => `${l.bundleId}x${l.qty}`).join(',')
    });
    return {
      ...s,
      primary: first,
      addOns: rest
    };
  });
  const checkout = () => {
    if (!order.length) return;
    const t = orderTotals(order);
    const contents = metaContents(order);
    track('begin_checkout', {
      value: t.total,
      currency: pricingConfig.currency,
      content_ids: contents.map(c => c.id),
      content_type: 'product',
      contents,
      num_items: t.lights
    });
    const url = buildCheckoutUrl(order);
    track('checkout_handoff', {
      value: t.total,
      url
    });
    window.location.assign(url);
  };
  const close = () => {
    if (state) track('precheckout_closed', {
      bundleId: state.primary.bundleId
    });
    setState(null);
  };
  return <FunnelContext.Provider value={{
    state,
    order,
    startSystem,
    startIndividual,
    upgradeTo,
    undoUpgrade,
    addSystem,
    setAddOnQty,
    replaceOrder,
    checkout,
    close
  }} data-id="element-1034" data-mp="1k2ynsmw2w6jc">
      {children}
    </FunnelContext.Provider>;
}
export function useFunnel() {
  const ctx = useContext(FunnelContext);
  if (!ctx) throw new Error('useFunnel must be used within FunnelProvider');
  return ctx;
}